import React, {useEffect, useMemo, useState} from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Stack,
    Step,
    StepLabel,
    Stepper,
    ToggleButton,
    ToggleButtonGroup,
    Typography
} from '@mui/material';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import IconButton from '@mui/material/IconButton';
import PropTypes from 'prop-types';
import AdmicityDialog from '../../../../shared-components/AdmicityDialog';
import PaginatedAutocomplete from '../../../../shared-components/PaginatedAutocomplete';
import AdmicityGroupFilterSelect from '../../../../shared-components/AdmicityGroupFilterSelect';
import AdmicityTableV2 from '../../../../shared-components/Table/V2/AdmicityTableV2';
import {useLazyGetAllEmployeeProfilesQuery} from '../../../../api/services/employeeService';
import {useLazyGetStudentFiltersQuery} from '../../../../api/services/studentService';
import {GROUP_MEMBERS_TYPE} from '../../../../constants/GroupMembersType';
import {maySaveRestriction} from './restrictionSaveGuard';

/* eslint-disable react/prop-types */

const CREATE_STEPS = ['Select subject', 'Exclude profiles'];

const getEmployeeDisplayName = (employee) =>
    employee?.displayName ||
    `${employee?.preferredForename || ''} ${employee?.preferredSurname || ''}`.trim();

const filterModulesByMembersType = (modules, membersType, nameHints = []) =>
    (modules || [])
        .filter(module =>
            module.membersType === membersType ||
            nameHints.some(hint => module.name?.toLowerCase().includes(hint)))
        .map(({filters, ...rest}) => ({
            ...rest,
            filters: (filters || []).filter(filter =>
                filter.membersType === membersType || filter.membersType == null)
        }))
        .filter(module => module.filters?.length);

const studentGroupColumns = [
    {
        accessorKey: 'name',
        header: 'Student group',
        grow: true
    }
];

const contentSizedTableProps = {
    layoutMode: 'grid-no-grow',
    enableSorting: false,
    enableFilters: false,
    enableHiding: false,
    enableFullScreenToggle: false,
    enableColumnResizing: false,
    enableBottomToolbar: false,
    enableTopToolbar: false,
    muiTablePaperProps: {
        sx: {
            flexGrow: 0,
        },
    },
    muiTableContainerProps: {
        sx: {
            flex: 'none',
            maxHeight: 'calc(56px + (5 * 52px))',
        },
    },
};

const emptyForm = () => ({
    subjectType: 'employee',
    employee: null,
    employeeGroup: null,
    students: [],
    applications: [],
    studentGroups: []
});

const StudentProfileAccessRestrictionDialog = (
    {
        open,
        mode,
        ruleId,
        initialValues,
        existingRules = [],
        groupFilterModules = [],
        isLoading = false,
        isSaving = false,
        onClose,
        onSave
    }) => {
    const [form, setForm] = useState(emptyForm);
    const [activeStep, setActiveStep] = useState(0);
    const [selectedStudentGroupFilter, setSelectedStudentGroupFilter] = useState(null);
    const isEdit = mode === 'edit';

    const employeeGroupModules = useMemo(
        () => filterModulesByMembersType(
            groupFilterModules,
            GROUP_MEMBERS_TYPE.employee,
            ['employees', 'school group', 'my group']
        ),
        [groupFilterModules]
    );

    const studentGroupModules = useMemo(
        () => filterModulesByMembersType(
            groupFilterModules,
            GROUP_MEMBERS_TYPE.students,
            []
        ).filter(module => module.name !== 'EMPLOYEES'),
        [groupFilterModules]
    );

    const existingEmployeeIds = useMemo(
        () => new Set(
            (existingRules || [])
                .filter(rule => rule.subjectType === 'employee' && rule.employeeId)
                .filter(rule => !isEdit || rule.id !== ruleId)
                .map(rule => rule.employeeId)
        ),
        [existingRules, isEdit, ruleId]
    );

    const existingEmployeeGroupFilterIds = useMemo(
        () => new Set(
            (existingRules || [])
                .filter(rule => rule.subjectType === 'employeeGroup' && rule.employeeGroupFilterId)
                .filter(rule => !isEdit || rule.id !== ruleId)
                .map(rule => rule.employeeGroupFilterId)
        ),
        [existingRules, isEdit, ruleId]
    );

    useEffect(() => {
        if (!open) {
            return;
        }

        if (initialValues) {
            setForm({
                subjectType: initialValues.subjectType || 'employee',
                employee: initialValues.employee || null,
                employeeGroup: initialValues.employeeGroup || null,
                students: initialValues.students || [],
                applications: initialValues.applications || [],
                studentGroups: initialValues.studentGroups || []
            });
        } else {
            setForm(emptyForm());
        }

        setActiveStep(isEdit ? 1 : 0);
        setSelectedStudentGroupFilter(null);
    }, [open, initialValues, ruleId, isEdit]);

    const subjectLabel = form.subjectType === 'employee'
        ? getEmployeeDisplayName(form.employee)
        : form.employeeGroup?.name;

    const hasSubject = form.subjectType === 'employee'
        ? Boolean(form.employee?.id)
        : Boolean(form.employeeGroup?.groupFilterId);

    const isDuplicateSubject = form.subjectType === 'employee'
        ? Boolean(form.employee?.id && existingEmployeeIds.has(form.employee.id))
        : Boolean(
            form.employeeGroup?.groupFilterId &&
            existingEmployeeGroupFilterIds.has(form.employeeGroup.groupFilterId)
        );

    const hasExclusions = form.students.length > 0 ||
        form.applications.length > 0 ||
        form.studentGroups.length > 0;

    const hasUnaddedStudentGroup = Boolean(selectedStudentGroupFilter?.filterId);

    const maySave = maySaveRestriction({
        hasSubject,
        hasExclusions,
        isDuplicateSubject,
        hasUnaddedStudentGroup
    });

    const handleClose = () => {
        if (isSaving) {
            return;
        }
        onClose();
    };

    const handleAddStudentGroup = () => {
        if (!selectedStudentGroupFilter?.filterId) {
            return;
        }

        const alreadyAdded = form.studentGroups.some(
            group => group.groupFilterId === selectedStudentGroupFilter.filterId
        );
        if (alreadyAdded) {
            setSelectedStudentGroupFilter(null);
            return;
        }

        const name = selectedStudentGroupFilter.paths?.map(path => path.name).join(' - ') ||
            selectedStudentGroupFilter.paths?.at(-1)?.name ||
            'Student group';

        setForm(prev => ({
            ...prev,
            studentGroups: [
                ...prev.studentGroups,
                {
                    id: selectedStudentGroupFilter.filterId,
                    groupFilterId: selectedStudentGroupFilter.filterId,
                    name
                }
            ]
        }));
        setSelectedStudentGroupFilter(null);
    };

    const handleSave = () => {
        if (!maySave) {
            return;
        }

        onSave({
            ruleId,
            subjectType: form.subjectType,
            employeeId: form.subjectType === 'employee' ? form.employee?.id : null,
            employeeGroupFilterId: form.subjectType === 'employeeGroup'
                ? form.employeeGroup?.groupFilterId
                : null,
            studentIds: [
                ...form.students.map(student => student.id),
                ...form.applications.map(application => application.id)
            ],
            studentGroupFilterIds: form.studentGroups.map(group => group.groupFilterId)
        });
    };

    const subjectStep = (
        <Stack spacing={2} sx={{mt: 1}}>
            <Typography variant="body2" color="text.secondary">
                Choose who this restriction applies to.
            </Typography>
            <ToggleButtonGroup
                exclusive
                size="small"
                color="primary"
                value={form.subjectType}
                onChange={(_event, value) => {
                    if (!value) {
                        return;
                    }
                    setForm(prev => ({
                        ...emptyForm(),
                        subjectType: value,
                        students: prev.students,
                        applications: prev.applications,
                        studentGroups: prev.studentGroups
                    }));
                }}
            >
                <ToggleButton value="employee" sx={{textTransform: 'none'}}>
                    Employee
                </ToggleButton>
                <ToggleButton value="employeeGroup" sx={{textTransform: 'none'}}>
                    Employee group
                </ToggleButton>
            </ToggleButtonGroup>

            {form.subjectType === 'employee' && (
                <PaginatedAutocomplete
                    multiple={false}
                    disablePortal={false}
                    textFieldProps={{
                        label: 'Employee',
                        placeholder: 'Enter name, surname, or full name',
                        helperText: isDuplicateSubject
                            ? 'A restriction already exists for this employee. Edit the existing rule instead.'
                            : 'Select one employee for this restriction',
                        error: isDuplicateSubject,
                    }}
                    fetchFn={useLazyGetAllEmployeeProfilesQuery}
                    getOptionLabel={getEmployeeDisplayName}
                    value={form.employee}
                    onChange={(_event, selected) => {
                        setForm(prev => ({
                            ...prev,
                            employee: selected
                                ? {
                                    ...selected,
                                    displayName: getEmployeeDisplayName(selected)
                                }
                                : null,
                            employeeGroup: null
                        }));
                    }}
                />
            )}

            {form.subjectType === 'employeeGroup' && (
                <>
                    <AdmicityGroupFilterSelect
                        isRequired={false}
                        sx={{width: '100%'}}
                        data={employeeGroupModules}
                        label="Employee group"
                        value={form.employeeGroup?.groupFilterId || ''}
                        onChange={(filterId, paths) => {
                            if (!filterId) {
                                setForm(prev => ({
                                    ...prev,
                                    employeeGroup: null,
                                    employee: null
                                }));
                                return;
                            }

                            const name = paths?.map(path => path.name).join(' - ') ||
                                paths?.at(-1)?.name ||
                                'Employee group';

                            setForm(prev => ({
                                ...prev,
                                employee: null,
                                employeeGroup: {
                                    groupFilterId: filterId,
                                    name
                                }
                            }));
                        }}
                    />
                    {isDuplicateSubject && (
                        <Typography variant="body2" color="error">
                            A restriction already exists for this employee group. Edit the existing rule instead.
                        </Typography>
                    )}
                </>
            )}
        </Stack>
    );

    const studentsStep = (
        <Stack spacing={2} sx={{mt: 1}}>
            <Box
                sx={{
                    px: 1.5,
                    py: 1,
                    borderRadius: 1,
                    backgroundColor: 'rgba(24, 107, 190, 0.06)'
                }}
            >
                <Typography variant="body2" color="text.secondary">
                    Restriction for
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                    {subjectLabel || '—'}
                    <Typography component="span" variant="body2" color="text.secondary" sx={{ml: 1}}>
                        ({form.subjectType === 'employee' ? 'Employee' : 'Employee group'})
                    </Typography>
                </Typography>
            </Box>

            <Typography variant="body2" color="text.secondary">
                Exclude at least one student group, student, or application.
            </Typography>
            {!hasExclusions && (
                <Typography variant="body2" color="error">
                    Add at least one exclusion before saving.
                </Typography>
            )}

            <Typography variant="subtitle2" fontWeight="bold">
                Excluded student groups
            </Typography>
            <AdmicityGroupFilterSelect
                isRequired={false}
                sx={{width: '100%'}}
                data={studentGroupModules}
                label="Student groups"
                value={selectedStudentGroupFilter?.filterId || ''}
                onChange={(filterId, paths) =>
                    setSelectedStudentGroupFilter(filterId ? {filterId, paths} : null)}
            />
            {hasUnaddedStudentGroup && (
                <Typography variant="body2" color="error">
                    Click Add group to exclude this group.
                </Typography>
            )}
            <Stack direction="row" justifyContent="end">
                <Button
                    variant="outlined"
                    disabled={!selectedStudentGroupFilter?.filterId}
                    onClick={handleAddStudentGroup}
                >
                    Add group
                </Button>
            </Stack>
            {form.studentGroups.length > 0 && (
                <AdmicityTableV2
                    columns={studentGroupColumns}
                    data={form.studentGroups}
                    hideFooterPagination
                    tableProps={{
                        ...contentSizedTableProps,
                        enableRowActions: true,
                        renderRowActions: ({row}) => (
                            <IconButton
                                size="small"
                                onClick={() => setForm(prev => ({
                                    ...prev,
                                    studentGroups: prev.studentGroups.filter(
                                        group => group.groupFilterId !==
                                            row.original.groupFilterId
                                    )
                                }))}
                            >
                                <DeleteOutlineRoundedIcon fontSize="small"/>
                            </IconButton>
                        )
                    }}
                />
            )}

            <Typography variant="subtitle2" fontWeight="bold">
                Excluded individual students
            </Typography>
            <PaginatedAutocomplete
                disablePortal={false}
                textFieldProps={{
                    label: 'Students',
                    placeholder: 'Enter name, surname, or full name',
                }}
                fetchFn={useLazyGetStudentFiltersQuery}
                fetchFnProps={{
                    queryParams: {
                        pupilType: 'student'
                    }
                }}
                getOptionLabel={(option) => option.label}
                value={form.students}
                onChange={(_event, newValue) => {
                    setForm(prev => ({
                        ...prev,
                        students: newValue || []
                    }));
                }}
            />

            <Typography variant="subtitle2" fontWeight="bold">
                Excluded individual applications
            </Typography>
            <PaginatedAutocomplete
                disablePortal={false}
                textFieldProps={{
                    label: 'Applications',
                    placeholder: 'Enter name, surname, or full name',
                }}
                fetchFn={useLazyGetStudentFiltersQuery}
                fetchFnProps={{
                    queryParams: {
                        pupilType: 'applicant'
                    }
                }}
                getOptionLabel={(option) => option.label}
                value={form.applications}
                onChange={(_event, newValue) => {
                    setForm(prev => ({
                        ...prev,
                        applications: newValue || []
                    }));
                }}
            />
        </Stack>
    );

    const createActions = activeStep === 0
        ? [
            {
                label: 'Cancel',
                onClick: handleClose,
                disabled: isSaving
            },
            {
                label: 'Next',
                onClick: () => setActiveStep(1),
                disabled: isSaving || isLoading || !hasSubject || isDuplicateSubject
            }
        ]
        : [
            {
                label: 'Back',
                onClick: () => setActiveStep(0),
                disabled: isSaving
            },
            {
                label: isSaving ? 'Saving...' : 'Save',
                onClick: handleSave,
                disabled: isSaving || isLoading || !maySave
            }
        ];

    const editActions = [
        {
            label: 'Cancel',
            onClick: handleClose,
            disabled: isSaving
        },
        {
            label: isSaving ? 'Saving...' : 'Save',
            onClick: handleSave,
            disabled: isSaving || isLoading || !maySave
        }
    ];

    return (
        <AdmicityDialog
            open={open}
            handleClose={handleClose}
            title={isEdit ? 'Edit student profile access restriction' : 'Add student profile access restriction'}
            maxWidth="md"
            contentText={
                isEdit
                    ? 'Update which students, applications, or groups this subject cannot open in detail.'
                    : activeStep === 0
                        ? 'Step 1 of 2 — select an employee or employee group.'
                        : 'Step 2 of 2 — exclude students, applications, or groups from profile detail access.'
            }
            actions={isEdit ? editActions : createActions}
        >
            {isLoading
                ? (
                    <Box display="flex" justifyContent="center" py={4}>
                        <CircularProgress size={32}/>
                    </Box>
                )
                : (
                    <>
                        {!isEdit && (
                            <Stepper
                                sx={{width: '100%', pb: 2}}
                                activeStep={activeStep}
                            >
                                {CREATE_STEPS.map(label => (
                                    <Step key={label}>
                                        <StepLabel>{label}</StepLabel>
                                    </Step>
                                ))}
                            </Stepper>
                        )}
                        {isEdit || activeStep === 1 ? studentsStep : subjectStep}
                    </>
                )}
        </AdmicityDialog>
    );
};

StudentProfileAccessRestrictionDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    mode: PropTypes.oneOf(['create', 'edit']).isRequired,
    ruleId: PropTypes.number,
    initialValues: PropTypes.object,
    existingRules: PropTypes.array,
    groupFilterModules: PropTypes.array,
    isLoading: PropTypes.bool,
    isSaving: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired
};

export default StudentProfileAccessRestrictionDialog;
